import React, { useState } from 'react';
import { Box, Card, CardActionArea, Avatar, Typography, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * Shared facade tile chrome (avatar initial + name + description + count + expand/collapse).
 *
 * The queue cards inside differ per page — HomePage and OrgBookingPage each have their own
 * CategoryCard with different props/styling — so the caller renders those as `children`; this
 * component owns only the tile and the collapse, so the two booking surfaces can't drift.
 */
export default function FacadeTile({ facade, count, defaultOpen = false, dense = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <CardActionArea onClick={() => setOpen((o) => !o)} sx={{ p: dense ? 2 : 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 44, height: 44, fontWeight: 700, flexShrink: 0,
              background: (t) => (t.palette.custom ? t.palette.custom.gradientPrimary : 'var(--gradient-primary)'),
            }}
          >
            {(facade.name || '?').trim().charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>{facade.name}</Typography>
            {facade.description ? (
              <Typography variant="body2" color="text.secondary" noWrap>{facade.description}</Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {count} option{count === 1 ? '' : 's'}
            </Typography>
          </Box>
          <ExpandMoreIcon sx={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', color: 'text.secondary' }} />
        </Box>
      </CardActionArea>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: dense ? 1.5 : 2, pb: 2, pt: 1, display: 'flex', flexDirection: 'column', gap: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {children}
        </Box>
      </Collapse>
    </Card>
  );
}
